import { CloseOutlined } from '@ant-design/icons';
import { TrashIcon } from '@heroicons/react/24/outline';
import { default as Divider } from 'antd/es/divider';
import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import type { Dispatch } from 'react';
import { useCallback, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { useDeleteCustomerCreditorsId, useGetCustomerCreditorsId } from 'api';
import { Restricted } from 'containers/Restricted';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { TRANSLATIONS } from './constants';
import { ReceiverDetailsList } from './ReceiverDetailsList';
import { showErrorNotification, showSuccessNotification } from './util';

import {
  RECEIVERS_DETAILS_CONFIRM_DELETE_BUTTON_TEST_ID,
  RECEIVERS_DETAILS_DELETE_BUTTON_TEST_ID,
  RECEIVERS_DETAILS_TEST_ID
} from '../constants';

type Props = {
  onClose: VoidFunction;
  receiverId: number;
  onDelete: Dispatch<void>;
};

export const ReceiverDetails = ({ onClose, receiverId, onDelete }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();
  const {
    data: { data: receiverData } = {},
    isLoading,
    isSuccess,
    isError
  } = useGetCustomerCreditorsId(organisationId, receiverId);

  const { mutate: destroyCreditorMutation } = useDeleteCustomerCreditorsId();

  const destroyCreditor = useCallback(() => {
    try {
      destroyCreditorMutation({ organisationId, id: receiverId });
      const fullName = receiverData?.full_name ?? 'Unknown Receiver';
      showSuccessNotification(openNotification, fullName);
    } catch (_) {
      showErrorNotification(openNotification, 'An error occured');
    } finally {
      onDelete();
      onClose();
      setIsDialogOpen(false);
    }
  }, [
    destroyCreditorMutation,
    onClose,
    onDelete,
    openNotification,
    organisationId,
    receiverData?.full_name,
    receiverId
  ]);

  return (
    <>
      <Modal
        open={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        centered
        styles={{
          mask: {
            backgroundColor: 'var(--tenant-surface-overlay)'
          },
          content: {
            width: 440,
            paddingTop: 12,
            backgroundColor: 'rgb(255, 255, 255)'
          }
        }}
        destroyOnHidden
        okText="Yes"
        okButtonProps={{ ['data-testid']: 'confirm-button' }}
        title={TRANSLATIONS.DESTROY_RECEIVER_TITLE}
        footer={
          <div className="flex w-full justify-between gap-6">
            <SecondaryButton
              className="flex-1"
              onClick={() => setIsDialogOpen(false)}
            >
              {TRANSLATIONS.NO}
            </SecondaryButton>
            <PrimaryButton
              danger
              onClick={destroyCreditor}
              data-testid={RECEIVERS_DETAILS_CONFIRM_DELETE_BUTTON_TEST_ID}
            >
              {TRANSLATIONS.YES}
            </PrimaryButton>
          </div>
        }
      >
        {TRANSLATIONS.ARE_YOU_SURE}
      </Modal>

      <div
        data-testid={RECEIVERS_DETAILS_TEST_ID}
        className="cool-shadow rounded-xl"
      >
        <div className="flex justify-between rounded-t-xl bg-white px-6 pt-3.5 text-xl font-medium">
          <span>Details</span>
          <TertiaryButton
            style={{ border: 'none', boxShadow: 'none' }}
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </div>
        <div className="flex flex-col bg-white p-6">
          {isLoading && <Skeleton />}
          {receiverData && isSuccess ? (
            <div>
              <div className="flex flex-col">
                <ReceiverDetailsList receiverData={receiverData} />
              </div>
              <Restricted requiredPermission="receivers:manage">
                <Divider />
                <SecondaryButton
                  className="flex w-full items-center justify-center"
                  danger
                  onClick={() => setIsDialogOpen(true)}
                  data-testid={RECEIVERS_DETAILS_DELETE_BUTTON_TEST_ID}
                >
                  <TrashIcon className="mr-2 size-6" />
                  Delete receiver
                </SecondaryButton>
              </Restricted>
            </div>
          ) : (
            <Skeleton />
          )}
          {isError && (
            <div className="flex h-full items-center justify-center">
              Something went wrong
            </div>
          )}
        </div>
      </div>
    </>
  );
};
